// Pure toggle logic for the lineup "highlight artist" feature,
// extracted from LineupImportTab so it can be unit-tested without
// React. The component owns the stage-filtering (which subset of the
// full timetable is shown for a given stage/day); this helper only
// flips the `highlighted` flag on one row of an already-filtered
// stage subset, immutably, and returns a fresh array.

export interface HighlightableRow {
  highlighted?: boolean;
}

/**
 * Return a copy of `rows` with the `highlighted` flag inverted on the
 * row at `index`. Out-of-range indices return the input unchanged.
 * Every other row is referentially preserved so React can skip
 * re-rendering untouched entries.
 */
export function toggleRowHighlight<T extends HighlightableRow>(rows: T[], index: number): T[] {
  if (index < 0 || index >= rows.length) return rows;
  return rows.map((row, i) => (i === index ? { ...row, highlighted: !row.highlighted } : row));
}

/**
 * Whether the green "important artist" glow should be painted for an artist
 * block. An artist glows only when the producer marked it (`highlighted`) AND
 * highlights aren't hidden via the schedule-creator toolbar toggle
 * (`showHighlights`).
 *
 * Shared by the schedule creator (`CrewSchedule`, which has the show/hide
 * toolbar toggle) and the read-only / share timeline (`ReadOnlyScheduleTimeline`,
 * which has no toggle and always passes `showHighlights = true`). Keeping this in
 * one place means both views agree on exactly when an artist is emphasised.
 */
export function artistGlowVisible(
  highlighted: boolean | null | undefined,
  showHighlights: boolean,
): boolean {
  return !!highlighted && showHighlights;
}
